import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ActivityIcon, AlertTriangleIcon, ClipboardCheckIcon, FileClockIcon, RocketIcon, ShieldAlertIcon } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader';
import { Panel } from '../../components/ui/Panel';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { EmptyState } from '../../components/ui/EmptyState';
import { ScopeSwitcher } from '../../components/admin/ScopeSwitcher';
import { useGovernance } from '../../contexts/GovernanceContext';
import { useTickets } from '../../contexts/TicketContext';
import { allCampuses, evaluateLaunch, scopeCovers } from '../../utils/governance';

/**
 * Admin home answers one question: what needs my attention?
 *
 * Every row is permission-aware and scope-aware, so two administrators signing
 * in see genuinely different work rather than the same list with disabled rows.
 */
export function AdminHome() {
  const {
    approvals,
    staff,
    invitations,
    switches,
    audit,
    launchChecks,
    tenants,
    activeScope,
    allows,
    me,
    scopeLabel
  } = useGovernance();
  const {
    tickets
  } = useTickets();
  const navigate = useNavigate();
  const pendingApprovals = approvals.filter(request => request.state === 'requested' && scopeCovers(activeScope, request.scope));
  const canSeeApprovals = allows('staff.approve').allowed || allows('payouts.approve').allowed;
  const expiredAccess = staff.filter(member => member.assignments.some(assignment => assignment.state === 'active' && assignment.expiresAt && new Date(assignment.expiresAt) < new Date()));
  const noMfa = staff.filter(member => member.status === 'active' && !member.mfaEnabled);
  const pendingInvites = invitations.filter(invitation => invitation.status === 'pending');
  const expiredInvites = invitations.filter(invitation => invitation.status === 'expired');
  const paused = switches.filter(item => item.state === 'paused');
  const openTickets = tickets.filter(ticket => ticket.status !== 'Resolved' && ticket.status !== 'Closed');
  const deniedAttempts = audit.filter(record => record.result === 'denied');
  const launchBlocked = allCampuses(tenants).filter(({
    tenant,
    campus
  }) => scopeCovers(activeScope, {
    level: 'campus',
    tenantId: tenant.id,
    campusId: campus.id,
    institutionId: tenant.institutions.find(institution => institution.campuses.some(item => item.id === campus.id))?.id
  })).map(({
    campus
  }) => evaluateLaunch(campus, launchChecks)).filter(outcome => outcome.campus.status !== 'live' && outcome.blockers.length > 0);
  const attention = [canSeeApprovals && pendingApprovals.length > 0 ? {
    id: 'approvals',
    tone: 'warn' as const,
    icon: ClipboardCheckIcon,
    title: `${pendingApprovals.length} request${pendingApprovals.length === 1 ? '' : 's'} waiting on an approver`,
    detail: pendingApprovals.map(request => request.summary).join(' · '),
    to: '/admin/governance',
    action: 'Open approvals'
  } : null, launchBlocked.length > 0 ? {
    id: 'launch',
    tone: 'danger' as const,
    icon: RocketIcon,
    title: `${launchBlocked.length} campus${launchBlocked.length === 1 ? '' : 'es'} cannot go live`,
    detail: launchBlocked.map(outcome => `${outcome.campus.name}: ${outcome.blockers.length} blocker${outcome.blockers.length === 1 ? '' : 's'}`).join(' · '),
    to: '/admin/launch',
    action: 'Open launch control'
  } : null, expiredAccess.length > 0 ? {
    id: 'expired',
    tone: 'warn' as const,
    icon: ShieldAlertIcon,
    title: `${expiredAccess.length} staff assignment${expiredAccess.length === 1 ? '' : 's'} past expiry`,
    detail: 'Access already stopped working. The records still need reviewing or renewing so the person is not silently locked out.',
    to: '/admin/governance',
    action: 'Open access review'
  } : null, noMfa.length > 0 ? {
    id: 'mfa',
    tone: 'warn' as const,
    icon: ShieldAlertIcon,
    title: `${noMfa.length} active administrator without MFA`,
    detail: noMfa.map(member => member.handle).join(' · '),
    to: '/admin/governance',
    action: 'Open staff'
  } : null, deniedAttempts.length > 0 ? {
    id: 'denied',
    tone: 'neutral' as const,
    icon: FileClockIcon,
    title: `${deniedAttempts.length} refused administrative attempt${deniedAttempts.length === 1 ? '' : 's'}`,
    detail: 'Refusals are logged like successes. Repeated attempts on the same record are worth a look.',
    to: '/admin/audit',
    action: 'Open audit log'
  } : null, openTickets.length > 0 ? {
    id: 'tickets',
    tone: 'brand' as const,
    icon: ActivityIcon,
    title: `${openTickets.length} open support ticket${openTickets.length === 1 ? '' : 's'}`,
    detail: openTickets.map(ticket => ticket.subject).slice(0, 2).join(' · '),
    to: '/admin/support',
    action: 'Open support desk'
  } : null].filter(Boolean) as {
    id: string;
    tone: 'warn' | 'danger' | 'brand' | 'neutral';
    icon: typeof RocketIcon;
    title: string;
    detail: string;
    to: string;
    action: string;
  }[];
  return <div className="space-y-6" data-id="element-5400" data-mp="2axnb4v6mhqk">
      <PageHeader eyebrow="Administration" title="What needs attention" description="Scoped to your operating context. Nothing here is a summary of the whole platform unless your access covers the whole platform." actions={<Badge tone="ok" plain data-id="element-5402" data-mp="27g3836aemzwz">
            Production environment
          </Badge>} data-id="element-5401" data-mp="6dc99xu5dxm2" />

      <Panel title="Your access" description="A role says what you may do. A scope says where. They are evaluated separately." data-id="element-5403" data-mp="23ft5m3ltmrmv">
        <ScopeSwitcher data-id="element-5404" data-mp="2ce4tvyz7tdc5" />
        <dl className="mt-5 grid gap-4 border-t border-line pt-4 sm:grid-cols-3" data-id="element-5405" data-mp="2cdljd3bw9r85">
          <div data-id="element-5406" data-mp="2r2rk4vqdrpi5">
            <dt className="text-body-sm text-ink-muted" data-id="element-5407" data-mp="2r28099h66r1h">Signed in as</dt>
            <dd className="mt-0.5 text-body font-semibold text-ink" data-id="element-5408" data-mp="2m4zhp7s40kzp">
              {me.name} <span className="cz-num font-normal text-ink-muted" data-id="element-5409" data-mp="3048tlv9a8sxv">{me.handle}</span>
            </dd>
          </div>
          <div data-id="element-5410" data-mp="2v4mjhuor3sta">
            <dt className="text-body-sm text-ink-muted" data-id="element-5411" data-mp="2v67dnj75l9qm">Active assignments</dt>
            <dd className="mt-0.5 text-body font-semibold text-ink" data-id="element-5412" data-mp="15pof60bk1tmx">
              {me.assignments.filter(assignment => assignment.state === 'active').length}
            </dd>
          </div>
          <div data-id="element-5413" data-mp="3ceqh2xu2d16f">
            <dt className="text-body-sm text-ink-muted" data-id="element-5414" data-mp="3ce6vn757funn">Looking at</dt>
            <dd className="mt-0.5 text-body font-semibold text-ink" data-id="element-5415" data-mp="3hdjt0ltpck4h">{scopeLabel(activeScope)}</dd>
          </div>
        </dl>
      </Panel>

      {attention.length === 0 ? <Panel title="Nothing outstanding" data-id="element-5416" data-mp="33k2b15gnzfgk">
          <EmptyState icon={<ClipboardCheckIcon className="h-5 w-5" data-id="element-5418" data-mp="37mfdnl046x94" />} title="No work waiting on you" description="Nothing in your scope needs an approval, review or decision right now." data-id="element-5417" data-mp="37n8btxl0cefn" />
        </Panel> : <ul className="grid gap-4 lg:grid-cols-2" data-id="element-5419" data-mp="2ykpcvokcelyo">
          {attention.map(item => {
        const Icon = item.icon;
        return <li key={item.id} data-id="element-5420" data-mp="2opxfoluoa14f">
                <Panel bodyClassName="p-5" data-id="element-5421" data-mp="2op5u83ushjrv">
                  <div className="flex items-start gap-3" data-id="element-5422" data-mp="ive9l3kg8s8r">
                    <span aria-hidden className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-canvas text-ink-muted" data-id="element-5423" data-mp="ittdh9ihl18s">
                      <Icon className="h-5 w-5" data-id="element-5424" data-mp="36ved4shn7n55" />
                    </span>
                    <div className="min-w-0" data-id="element-5425" data-mp="31v8s3v5voyw1">
                      <div className="flex flex-wrap items-center gap-2" data-id="element-5426" data-mp="2x38mbm34nbrj">
                        <p className="text-body-lg font-semibold text-ink" data-id="element-5427" data-mp="2x1e3qefbvvqm">{item.title}</p>
                        {item.tone === 'danger' && <Badge tone="danger" icon={<AlertTriangleIcon className="h-4 w-4" data-id="element-5429" data-mp="2o4dg4a0321o8" />} data-id="element-5428" data-mp="2o43neennlgeu">
                            Blocker
                          </Badge>}
                      </div>
                      <p className="mt-1.5 max-w-[62ch] text-body-sm text-ink-muted" data-id="element-5430" data-mp="2e5eg1ftdvecu">{item.detail}</p>
                      <Button variant="secondary" size="sm" className="mt-3" onClick={() => navigate(item.to)} data-id="element-5431" data-mp="297cqcc1gcgse">
                        {item.action}
                      </Button>
                    </div>
                  </div>
                </Panel>
              </li>;
      })}
        </ul>}

      <div className="grid gap-5 lg:grid-cols-2" data-id="element-5432" data-mp="onr0obv4n4vw">
        <Panel title="Paused capabilities" description="Paused, never deleted. Existing records continue through controlled recovery." bodyClassName="p-0" data-id="element-5433" data-mp="op23a8cu66lf">
          {paused.length === 0 ? <p className="px-5 py-4 text-body-sm text-ink-muted" data-id="element-5434" data-mp="3t41upqhznx80">Nothing is currently paused.</p> : <ul className="divide-y divide-line" data-id="element-5435" data-mp="5x9aof1oine8">
              {paused.map(item => <li key={item.id} className="px-5 py-3.5" data-id="element-5436" data-mp="5zm1lhpf083f">
                  <div className="flex flex-wrap items-center justify-between gap-2" data-id="element-5437" data-mp="ax5f2feaggub">
                    <p className="text-body font-medium text-ink" data-id="element-5438" data-mp="1hbd51mc6idjt">{item.label}</p>
                    <Badge tone="warn" data-id="element-5439" data-mp="1mazr8l9zj44r">Paused</Badge>
                  </div>
                  <p className="mt-1 max-w-[62ch] text-body-sm text-ink-muted" data-id="element-5440" data-mp="2dcdhd6gliiut">{item.effect}</p>
                </li>)}
            </ul>}
        </Panel>

        <Panel title="Invitations" bodyClassName="p-0" data-id="element-5441" data-mp="2i9lxl1i6octf">
          <ul className="divide-y divide-line" data-id="element-5442" data-mp="2n1x08c4b6cpo">
            <li className="flex items-center justify-between px-5 py-3.5" data-id="element-5443" data-mp="2n49placebpcr">
              <p className="text-body text-ink" data-id="element-5444" data-mp="2r38uiuy4nm47">Pending acceptance</p>
              <p className="cz-num text-body-lg font-semibold text-ink" data-id="element-5445" data-mp="1ue9kpbhwac9w">{pendingInvites.length}</p>
            </li>
            <li className="flex items-center justify-between px-5 py-3.5" data-id="element-5446" data-mp="1zddrxwl9dzxl">
              <p className="text-body text-ink" data-id="element-5447" data-mp="23do5pvfalun0">Expired without acceptance</p>
              <p className="cz-num text-body-lg font-semibold text-ink" data-id="element-5448" data-mp="28d98kgxl2n50">{expiredInvites.length}</p>
            </li>
          </ul>
          <p className="border-t border-line px-5 py-3.5 text-body-sm text-ink-muted" data-id="element-5449" data-mp="3eu4fb8461h1y">
            An expired invitation grants nothing. The token is single-use and stops working on its
            expiry whether or not anyone revokes it.
          </p>
        </Panel>
      </div>
    </div>;
}